import React from 'react';
import useProgramContent from '@/components/admin/ProgramManagement/AddLessons/useProgramContent';
import useProgramContentStore from '@/components/admin/ProgramManagement/AddLessons/useProgramContentStore';
import ILesson from '@/types/api/Lessons';
import { IUnit } from '@/components/admin/ProgramManagement/types';
import SPACheckbox from '../../shared/SPACheckbox';
import SPARadio from '../../shared/SPARadio';

// AIDEV-NOTE: SPA counterpart of ProgramManagement/AddLessons' ReuseProgramContent tree —
// same useProgramContentStore selection logic, SPA form primitives. The legacy components are
// still used by the /admin programs page.

const rowClass = 'tw-flex tw-items-center tw-gap-4 tw-py-1';
const radioGroupClass = 'tw-flex tw-items-center tw-gap-4 tw-ml-auto tw-shrink-0';

const ContentSkeleton = () => (
  <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-2">
    {Array.from({ length: 8 }, (_, i) => (
      <div key={i} className="tw-h-5 tw-bg-spa-bg-skeleton-subtle tw-rounded tw-animate-pulse" />
    ))}
  </div>
);

const LessonRow = ({ lesson }: { lesson: ILesson }) => {
  const { id, title_with_position: title } = lesson;
  const reuseIds = useProgramContentStore(state => state.reuseLessonIds);
  const copyIds = useProgramContentStore(state => state.copyLessonIds);
  const addId = useProgramContentStore(state => state.addLessonId);
  const removeId = useProgramContentStore(state => state.removeLessonId);
  const parentLessonIds = useProgramContentStore(state => state.parentLessonIds);
  const isChecked = reuseIds.includes(id) || copyIds.includes(id);

  return (
    <div className={rowClass}>
      <SPACheckbox
        checked={isChecked}
        disabled={parentLessonIds.includes(id)}
        onChange={() => {
          if (isChecked) {
            removeId('copy', id);
            removeId('reuse', id);
          } else {
            addId('reuse', id);
          }
        }}
      >
        {title}
      </SPACheckbox>

      {isChecked && (
        <div className={radioGroupClass}>
          <SPARadio
            checked={reuseIds.includes(id)}
            name={`lesson-${id}-action`}
            onChange={() => {
              addId('reuse', id);
              removeId('copy', id);
            }}
          >
            Reuse
          </SPARadio>
          <SPARadio
            checked={copyIds.includes(id)}
            name={`lesson-${id}-action`}
            onChange={() => {
              addId('copy', id);
              removeId('reuse', id);
            }}
          >
            Copy
          </SPARadio>
        </div>
      )}
    </div>
  );
};

const UnitRow = ({ unit }: { unit: IUnit & { lessons: ILesson[] } }) => {
  const { id, title_with_position: title } = unit;
  const parentUnitIds = useProgramContentStore(state => state.parentUnitIds);
  const parentLessonIds = useProgramContentStore(state => state.parentLessonIds);
  const reuseIds = useProgramContentStore(state => state.reuseUnitIds);
  const copyIds = useProgramContentStore(state => state.copyUnitIds);
  const addId = useProgramContentStore(state => state.addUnitId);
  const removeId = useProgramContentStore(state => state.removeUnitId);
  const anyLessonsChecked = useProgramContentStore(state => state.anyLessonsChecked);
  const allLessonsChecked = useProgramContentStore(state => state.allLessonsChecked);
  const toggleAllLessons = useProgramContentStore(state => state.toggleAllLessons);

  const lessonIds = unit.lessons.map(l => l.id).filter(lessonId => !parentLessonIds.includes(lessonId));
  const disabled = parentUnitIds.includes(id);
  const isChecked = allLessonsChecked(lessonIds);
  const isIndeterminate = reuseIds.includes(id) || copyIds.includes(id) || anyLessonsChecked(lessonIds);

  return (
    <div className={rowClass}>
      <SPACheckbox
        checked={isChecked}
        disabled={disabled}
        indeterminate={isIndeterminate && !isChecked}
        labelClassName="tw-font-medium tw-text-spa-heading"
        onChange={() => {
          if (isChecked || isIndeterminate) {
            removeId('copy', id);
            removeId('reuse', id);
          } else {
            addId('reuse', id);
          }
          toggleAllLessons(lessonIds);
        }}
      >
        {title}
      </SPACheckbox>

      {(isChecked || isIndeterminate) && (
        <div className={radioGroupClass}>
          <SPARadio
            checked={reuseIds.includes(id)}
            disabled={disabled}
            name={`unit-${id}-action`}
            onChange={() => {
              addId('reuse', id);
              removeId('copy', id);
            }}
          >
            Reuse
          </SPARadio>
          <SPARadio
            checked={copyIds.includes(id)}
            disabled={disabled}
            name={`unit-${id}-action`}
            onChange={() => {
              addId('copy', id);
              removeId('reuse', id);
            }}
          >
            Copy
          </SPARadio>
          <SPARadio
            checked={!reuseIds.includes(id) && !copyIds.includes(id)}
            name={`unit-${id}-action`}
            onChange={() => {
              removeId('copy', id);
              removeId('reuse', id);
            }}
          >
            Do Not Use
          </SPARadio>
        </div>
      )}
    </div>
  );
};

const SPAReuseContent = ({ programId }: { programId: number }) => {
  const { isLoading, units, lessons } = useProgramContent({ programId });
  const allLessonsChecked = useProgramContentStore(state => state.allLessonsChecked);
  const toggleAllLessons = useProgramContentStore(state => state.toggleAllLessons);
  const toggleLessonOn = useProgramContentStore(state => state.toggleLessonOn);
  const toggleLessonOff = useProgramContentStore(state => state.toggleLessonOff);
  const toggleUnitOn = useProgramContentStore(state => state.toggleUnitOn);
  const toggleUnitOff = useProgramContentStore(state => state.toggleUnitOff);

  const allUnitsChecked = () => units.every(unit => allLessonsChecked(unit.lessons.map(l => l.id)));

  const allChecked = units.length > 0 ? allUnitsChecked() : allLessonsChecked(lessons.map(l => l.id));

  const onToggleAll = () => {
    if (units.length === 0) {
      toggleAllLessons(lessons.map(l => l.id));
      return;
    }

    const clearing = allUnitsChecked();
    units.forEach((unit) => {
      if (clearing) toggleUnitOff(unit.id);
      else toggleUnitOn(unit.id);
      unit.lessons.forEach(lesson => (clearing ? toggleLessonOff(lesson.id) : toggleLessonOn(lesson.id)));
    });
  };

  return (
    <div className="tw-mt-4">
      <div className="tw-text-sm tw-font-semibold tw-text-spa-heading tw-mb-2">
        {isLoading || units.length > 0 ? 'Units and Lessons' : 'Lessons'}
      </div>

      {isLoading && <ContentSkeleton />}

      {!isLoading && (
        <>
          <SPACheckbox
            checked={allChecked}
            labelClassName="tw-font-medium"
            onChange={onToggleAll}
          >
            Select All
          </SPACheckbox>
          <div className="tw-mt-2">
            {units.length > 0 && units.map(unit => (
              <div key={unit.id}>
                <UnitRow unit={unit} />
                <div className="tw-ml-6">
                  {unit.lessons.map(lesson => <LessonRow key={lesson.id} lesson={lesson} />)}
                </div>
              </div>
            ))}
            {units.length === 0 && lessons.map(lesson => <LessonRow key={lesson.id} lesson={lesson} />)}
          </div>
        </>
      )}
    </div>
  );
};

export default SPAReuseContent;
